'use client';

import Link from 'next/link';

import type { GeneralLayout } from '@eliadmin/core/src/api/general/types/general-layout-types';

import { Paragraph } from '@eliadmin/core/src/components/ui/paragraph';
import { MdOutlinePhoneInTalk } from 'react-icons/md';
import { useBreakpoint } from '@eliadmin/core/src/hooks/use-breakpoint';

const MARQUEE_COPIES = 4;

function NotificationContent({
  generalLayout,
  ariaHidden,
}: {
  generalLayout: GeneralLayout['data'];
  ariaHidden?: boolean;
}) {
  return (
    <div
      aria-hidden={ariaHidden}
      inert={ariaHidden || undefined}
      className="flex shrink-0 flex-row items-center gap-2 whitespace-nowrap pr-8"
    >
      <Paragraph
        innerHtml="<p>Sme tu pre vás každý pracovný deň od <strong>7:00</strong> do <strong>16:00.</strong></p>"
        color="white"
        size="base"
        className="whitespace-nowrap text-center"
      />
      <MdOutlinePhoneInTalk size={18} className="text-white" />
      <Paragraph color="white" size="base" className="flex whitespace-nowrap">
        Infolinka:&nbsp;
        <Link
          prefetch={false}
          href={`tel:${generalLayout?.contact?.phone}`}
          target="_blank"
          className="font-bold transition-all hover:text-white/90"
        >
          {generalLayout?.contact?.phone}
        </Link>
        &nbsp;alebo e-mail:&nbsp;
        <Link
          prefetch={false}
          target="_blank"
          href={`mailto:${generalLayout?.contact?.email}`}
          className="font-bold transition-all hover:text-white/90"
        >
          {generalLayout?.contact?.email}
        </Link>
      </Paragraph>
    </div>
  );
}

export function HeaderNotification({ generalLayout }: { generalLayout: GeneralLayout['data'] }) {
  const isDesktop = useBreakpoint('lg');

  if (isDesktop) {
    return (
      <div className="ea-saprem-notification relative z-30 flex h-[30px] w-full flex-row items-center justify-center gap-2 bg-primary px-4 sm:h-[32px] lg:h-[37px]">
        <Paragraph
          innerHtml="<p>Sme tu pre vás každý pracovný deň od <strong>7:00</strong> do <strong>16:00.</strong></p>"
          color="white"
          size="base"
          className="whitespace-nowrap text-center"
        />
        <MdOutlinePhoneInTalk size={18} className="text-white" />
        <Paragraph color="white" size="base" className="flex">
          Infolinka:&nbsp;
          <Link
            prefetch={false}
            href={`tel:${generalLayout?.contact?.phone}`}
            target="_blank"
            className="font-bold transition-all hover:text-white/90"
          >
            {generalLayout?.contact?.phone}
          </Link>
          &nbsp;alebo e-mail:&nbsp;
          <Link
            prefetch={false}
            target="_blank"
            href={`mailto:${generalLayout?.contact?.email}`}
            className="font-bold transition-all hover:text-white/90"
          >
            {generalLayout?.contact?.email}
          </Link>
        </Paragraph>
      </div>
    );
  }

  return (
    <div className="ea-saprem-notification relative z-30 h-[30px] w-full overflow-hidden bg-primary px-4 sm:h-[32px]">
      <style>{`
        @keyframes ea-notification-marquee {
          from { transform: translateX(0); }
          to   { transform: translateX(calc(-100% / ${MARQUEE_COPIES})); }
        }
        .ea-notification-track {
          display: flex;
          flex-direction: row;
          align-items: center;
          height: 100%;
          width: max-content;
          will-change: transform;
          animation: ea-notification-marquee ${MARQUEE_COPIES * 5}s linear infinite;
        }
        .ea-notification-track:focus-within,
        .ea-notification-track:hover { animation-play-state: paused; }
        @media (prefers-reduced-motion: reduce) {
          .ea-notification-track { animation: none; }
        }
      `}</style>
      <div className="ea-notification-track">
        {Array.from({ length: MARQUEE_COPIES }).map((_, i) => (
          <NotificationContent key={i} generalLayout={generalLayout} ariaHidden={i > 0} />
        ))}
      </div>
    </div>
  );
}
